import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { Icon } from '@/components/readiness/primitives'

// ── FilterSelect (Iteration 4 §1) ───────────────────────────────────────────
// A trigger button and a popover listbox in place of the native <select>,
// which sizes to its longest option. The trigger reads `{Label}: {Value}`,
// stays 140–280px wide and truncates the value; it never widens. Keyboard
// behaviour matches a native select: Enter / Space / ArrowDown open, ArrowUp /
// ArrowDown move, Home / End jump, type-ahead, Enter selects, Escape closes
// and returns focus to the trigger.
//
// `any` prepends an `Any` option with the empty value. The trigger takes the
// `on` style whenever the value differs from the default (`Any` / `All`);
// controls without a default (Group by, Sort) never do.

export type FilterOption = [value: string, label: string]

interface BaseProps {
  label: string
  options: FilterOption[]
  any?: boolean
  anyLabel?: string
  compact?: boolean
  testId?: string
  className?: string
}

type SingleProps = BaseProps & { multiple?: false; value: string; onChange: (v: string) => void; defaultValue?: string }
type MultiProps = BaseProps & { multiple: true; value: string[]; onChange: (v: string[]) => void; defaultValue?: never }

export function FilterSelect(props: SingleProps | MultiProps) {
  const { label, any = false, anyLabel = 'Any', compact = false, testId, className = '' } = props
  const options: FilterOption[] = any && !props.multiple ? [['', anyLabel], ...props.options] : props.options
  const id = useId()
  const listId = `${id}-list`
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const typed = useRef<{ s: string; t: number }>({ s: '', t: 0 })
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  const selected = (v: string) => (props.multiple ? props.value.includes(v) : props.value === v)
  const valueText = props.multiple
    ? props.value.length
      ? props.options.filter(([v]) => props.value.includes(v)).map(([, l]) => l).join(', ')
      : 'All'
    : options.find(([v]) => v === props.value)?.[1] ?? props.value
  const defaultValue = props.multiple ? null : props.defaultValue ?? (any ? '' : null)
  const on = props.multiple ? props.value.length > 0 : defaultValue != null && props.value !== defaultValue

  const show = () => {
    const i = options.findIndex(([v]) => selected(v))
    setActive(i < 0 ? 0 : i)
    setOpen(true)
  }
  const close = (focus = true) => {
    setOpen(false)
    if (focus) triggerRef.current?.focus()
  }
  const choose = (i: number) => {
    const opt = options[i]
    if (!opt) return
    if (props.multiple) {
      const has = props.value.includes(opt[0])
      props.onChange(has ? props.value.filter((x) => x !== opt[0]) : [...props.value, opt[0]])
      setActive(i)
    } else {
      props.onChange(opt[0])
      close()
    }
  }

  useEffect(() => {
    if (!open) return
    listRef.current?.focus()
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const typeAhead = (key: string) => {
    const now = Date.now()
    typed.current = { s: now - typed.current.t < 600 ? typed.current.s + key.toLowerCase() : key.toLowerCase(), t: now }
    const q = typed.current.s
    const start = q.length === 1 ? active + 1 : active
    for (let k = 0; k < options.length; k++) {
      const i = (start + k) % options.length
      if (options[i][1].toLowerCase().startsWith(q)) return i
    }
    return -1
  }

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      show()
    } else if (e.key.length === 1 && /\S/.test(e.key) && !props.multiple) {
      // Native selects change value on type-ahead while closed.
      const i = typeAhead(e.key)
      if (i >= 0) props.onChange(options[i][0])
    }
  }
  const onListKey = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(options.length - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)) }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0) }
    else if (e.key === 'End') { e.preventDefault(); setActive(options.length - 1) }
    else if (e.key === 'Enter' || (e.key === ' ' && props.multiple)) { e.preventDefault(); choose(active) }
    else if (e.key === 'Escape') { e.preventDefault(); close() }
    else if (e.key === 'Tab') close(false)
    else if (e.key.length === 1 && /\S/.test(e.key)) {
      const i = typeAhead(e.key)
      if (i >= 0) setActive(i)
    }
  }

  return (
    <span ref={wrapRef} className={`relative inline-flex shrink-0 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`rd-trigger ${compact ? 'rd-trigger-compact' : ''} ${on ? 'rd-on' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${valueText}`}
        onClick={() => (open ? close() : show())}
        onKeyDown={onTriggerKey}
        data-testid={testId ?? `filter-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        data-value={props.multiple ? props.value.join(',') : props.value}
      >
        <span className="rd-trigger-label">{label}:</span>
        <span className="min-w-0 truncate">{valueText}</span>
        <Icon name="chevron" size={14} className="rd-trigger-chevron" />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={label}
          aria-multiselectable={props.multiple || undefined}
          aria-activedescendant={`${id}-opt-${active}`}
          className="rd-popover"
          style={{ minWidth: triggerRef.current?.offsetWidth }}
          onKeyDown={onListKey}
        >
          {options.map(([v, l], i) => (
            <li
              key={v || '__any'}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={selected(v)}
              data-index={i}
              data-active={i === active || undefined}
              className="rd-option"
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(i)}
            >
              <span className="rd-option-tick" aria-hidden="true">{selected(v) && <Icon name="check" size={14} />}</span>
              <span className="min-w-0 truncate">{l}</span>
            </li>
          ))}
        </ul>
      )}
    </span>
  )
}
